import type { Product } from '../types';
import { trackSelectItem } from './ga4Ecommerce';
import {
  classifyAttribution,
  mergeVisitorTouch,
  shouldIgnoreReferrer,
  touchFromClassified,
  type AttributionInput,
  type BookingAttributionSnapshot,
  type ClassifiedAttribution,
  type TouchAttribution,
  type VisitorTouchState,
} from './attribution';
import { normalizeAnalyticsPath } from './analyticsPath';
import {
  captureNewsletterTouchFromWindow,
  getActiveNewsletterTouch,
  newsletterTouchToAttributionSlice,
} from './newsletterAttribution';

export type AnalyticsEventType =
  | 'page_view'
  | 'product_view'
  | 'product_click'
  | 'add_to_cart'
  | 'begin_checkout'
  | 'checkout_step_view'
  | 'checkout_error'
  | 'submit_booking'
  | 'purchase'
  | 'search'
  | 'faq_expand'
  | 'contact_submit'
  | 'quote_request'
  | 'quote_cta_click'
  | 'quote_modal_open'
  | 'quote_form_started'
  | 'quote_form_submitted'
  | 'quote_form_error'
  | 'quote_form_closed'
  | 'chatbot_open'
  | 'chatbot_human_request'
  | 'customer_type_select'
  | 'cta_click'
  | 'wizard_step'
  | 'wizard_abandon'
  | 'wizard_complete'
  | 'landing_view'
  | 'portal_upsell_view'
  | 'portal_upsell_add_click'
  | 'portal_upsell_add_success'
  | 'booking_view'
  | 'booking_cta_click'
  | 'foretag_need_cards_view'
  | 'foretag_need_card_click'
  /** Phase 2A /foretag commercial funnel (also mirrored as metadata.funnel_event on cta/quote). */
  | 'foretag_hero_cta_click'
  | 'planning_call_start'
  | 'quote_start'
  | 'specialist_navigation'
  | 'phone_click'
  | 'email_click';

const PRODUCT_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Canonical product UUID only — never route templates like ":id". */
export function isValidAnalyticsProductId(productId: unknown): productId is string {
  if (typeof productId !== 'string') return false;
  const id = productId.trim();
  if (!id || id === ':id' || id.includes(':')) return false;
  return PRODUCT_ID_UUID_RE.test(id);
}

export type QuoteFormCloseMethod = 'x' | 'backdrop' | 'cancel';

export type QuoteFormAnalyticsContext = {
  productId?: string | null;
  productName?: string | null;
  packageType?: string | null;
  eventType?: string | null;
  variant?: 'modal' | 'inline' | 'b2b_event';
  quoteId?: string;
  closeMethod?: QuoteFormCloseMethod;
  partner?: string | null;
  budgetSegment?: string | null;
  ctaContext?: string | null;
};

type AnalyticsProductInput = Pick<Product, 'id' | 'name' | 'category' | 'slug'>;

type QueuedEvent = {
  event_type: AnalyticsEventType;
  path: string;
  product_id?: string | null;
  session_id: string;
  referrer_path?: string | null;
  metadata?: Record<string, unknown>;
};

const EXCLUDED_PREFIXES = ['/admin', '/personal', '/dj-portal', '/leverantor'];
const SESSION_KEY = 'analyticsSessionId';
const ATTRIBUTION_KEY = 'analyticsAttribution';
const VISITOR_ID_KEY = 'analyticsVisitorId';
const VISITOR_TOUCH_KEY = 'analyticsVisitorTouch';
const INTERNAL_EXCLUDE_KEY = 'analyticsInternalExclude';
const FLUSH_DELAY_MS = 2000;

export type AnalyticsAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  external_referrer?: string;
  referrer_host?: string;
  landing_path?: string;
  channel?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  attribution_confidence?: string;
  visitor_id?: string;
  captured_at?: string;
};

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
] as const;

let eventQueue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let lastTrackedPath = '';

export function isTrackablePath(path: string): boolean {
  return !EXCLUDED_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function isInternalAnalyticsExcluded(): boolean {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(INTERNAL_EXCLUDE_KEY) === 'admin';
}

export function setInternalAnalyticsExcluded(excluded: boolean): void {
  if (typeof window === 'undefined') return;
  if (excluded) {
    sessionStorage.setItem(INTERNAL_EXCLUDE_KEY, 'admin');
  } else {
    sessionStorage.removeItem(INTERNAL_EXCLUDE_KEY);
  }
}

/** First-party analytics + GA4/dataLayer ska inte spåras för inloggade admins. */
export function shouldTrackAnalytics(): boolean {
  return hasAnalyticsConsent() && !isInternalAnalyticsExcluded();
}

export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const raw = localStorage.getItem('cookieConsent');
    if (!raw) return false;
    const consent = JSON.parse(raw) as { analytics?: boolean };
    return consent.analytics === true;
  } catch {
    return false;
  }
}

export function getOrCreateAnalyticsVisitorId(): string {
  if (typeof window === 'undefined') return '';
  if (!hasAnalyticsConsent()) return '';

  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
}

function readVisitorTouch(): VisitorTouchState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(VISITOR_TOUCH_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as VisitorTouchState;
  } catch {
    return null;
  }
}

function writeVisitorTouch(state: VisitorTouchState): void {
  if (typeof window === 'undefined') return;
  if (!hasAnalyticsConsent()) return;
  localStorage.setItem(VISITOR_TOUCH_KEY, JSON.stringify(state));
}

function toAttributionInput(attr: AnalyticsAttribution): AttributionInput {
  return {
    referrerHost: attr.referrer_host,
    externalReferrer: attr.external_referrer,
    landingPath: attr.landing_path,
    utmSource: attr.utm_source,
    utmMedium: attr.utm_medium,
    utmCampaign: attr.utm_campaign,
    utmContent: attr.utm_content,
    utmTerm: attr.utm_term,
    gclid: attr.gclid,
    gbraid: attr.gbraid,
    wbraid: attr.wbraid,
    fbclid: attr.fbclid,
  };
}

export function getOrCreateAnalyticsSessionId(): string {
  if (typeof window === 'undefined') return '';

  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
    captureAttributionOnce();
  }
  return id;
}

/** Fånga UTM/click IDs och extern referrer vid sessionens första sidvisning. */
export function captureAttributionOnce(): AnalyticsAttribution | null {
  // Kampanjtouch från nl_*/utm — consent-oberoende (som partner-lead); events kräver fortfarande consent.
  captureNewsletterTouchFromWindow();
  if (typeof window === 'undefined') return null;
  if (sessionStorage.getItem(ATTRIBUTION_KEY)) {
    try {
      return JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY)!) as AnalyticsAttribution;
    } catch {
      return null;
    }
  }

  const params = new URLSearchParams(window.location.search);
  const attribution: AnalyticsAttribution = {
    landing_path: window.location.pathname.split('#')[0] || '/',
    captured_at: new Date().toISOString(),
  };

  for (const key of UTM_PARAMS) {
    const value = params.get(key)?.trim();
    if (value) {
      (attribution as Record<string, string>)[key] = value.slice(0, 500);
    }
  }

  if (document.referrer) {
    try {
      const refUrl = new URL(document.referrer);
      if (refUrl.origin !== window.location.origin) {
        const host = refUrl.hostname.slice(0, 200);
        if (!shouldIgnoreReferrer(host)) {
          attribution.external_referrer = document.referrer.slice(0, 500);
          attribution.referrer_host = host;
        }
        // Stripe/self-referral: leave referrer empty → keep prior visitor touch; session = Direct or UTM
      }
    } catch {
      /* ignore */
    }
  }

  const classified = classifyAttribution(toAttributionInput(attribution));
  attribution.channel = classified.channel;
  attribution.source = classified.source;
  attribution.medium = classified.medium;
  attribution.campaign = classified.campaign ?? undefined;
  attribution.attribution_confidence = classified.confidence;

  if (hasAnalyticsConsent()) {
    const visitorId = getOrCreateAnalyticsVisitorId();
    attribution.visitor_id = visitorId || undefined;
    const nextTouch = mergeVisitorTouch(
      readVisitorTouch(),
      classified,
      toAttributionInput(attribution)
    );
    writeVisitorTouch(nextTouch);
  }

  // Always persist (including Direct) so landing_path + classification reach the server
  sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  return attribution;
}

function getStoredAttribution(): AnalyticsAttribution {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as AnalyticsAttribution;
  } catch {
    return {};
  }
}

function getSessionClassified(): ClassifiedAttribution {
  const attr = getStoredAttribution();
  if (attr.channel && attr.source) {
    return {
      channel: attr.channel as ClassifiedAttribution['channel'],
      source: attr.source,
      medium: attr.medium || '(none)',
      campaign: attr.campaign ?? null,
      confidence: (attr.attribution_confidence as ClassifiedAttribution['confidence']) || 'high',
    };
  }
  return classifyAttribution(toAttributionInput(attr));
}

/** Snapshot for rental_requests.attribution_snapshot at booking time. */
export function getAttributionForBooking(): {
  analytics_session_id: string | null;
  analytics_visitor_id: string | null;
  attribution_snapshot: BookingAttributionSnapshot | null;
} {
  captureNewsletterTouchFromWindow();
  const newsletter = getActiveNewsletterTouch();
  const newsletterSlice = newsletter ? newsletterTouchToAttributionSlice(newsletter) : null;

  if (typeof window === 'undefined') {
    return {
      analytics_session_id: null,
      analytics_visitor_id: null,
      attribution_snapshot: null,
    };
  }

  // Utan analytics-consent: ingen beteendespårning, men verifierad kampanjtouch från URL får följa med affären.
  if (!hasAnalyticsConsent()) {
    if (!newsletterSlice) {
      return {
        analytics_session_id: null,
        analytics_visitor_id: null,
        attribution_snapshot: null,
      };
    }
    const emailTouch: TouchAttribution = {
      source: 'newsletter',
      medium: 'email',
      channel: 'email',
      campaign: newsletterSlice.utm_campaign || newsletterSlice.campaign_id,
      utmSource: 'newsletter',
      utmMedium: 'email',
      utmCampaign: newsletterSlice.utm_campaign || newsletterSlice.campaign_id,
      utmContent: newsletterSlice.link_key,
      at: newsletterSlice.captured_at,
    };
    return {
      analytics_session_id: null,
      analytics_visitor_id: null,
      attribution_snapshot: {
        first: emailTouch,
        last_non_direct: emailTouch,
        conversion: emailTouch,
        newsletter: newsletterSlice,
      } as BookingAttributionSnapshot,
    };
  }

  const sessionId = getOrCreateAnalyticsSessionId() || null;
  const visitorId = getOrCreateAnalyticsVisitorId() || null;
  const conversion = getSessionClassified();
  const attr = getStoredAttribution();
  const touch = readVisitorTouch();
  const conversionTouch = touchFromClassified(conversion, toAttributionInput(attr));
  /** Prefer captured landing_path; never invent historical landings without evidence. */
  const ensureLanding = (t: TouchAttribution): TouchAttribution => ({
    ...t,
    landingPage:
      t.landingPage ||
      attr.landing_path ||
      (typeof window !== 'undefined'
        ? window.location.pathname.split('#')[0] || null
        : null),
  });

  const snapshot: BookingAttributionSnapshot & { newsletter?: typeof newsletterSlice } = {
    first: ensureLanding(touch?.first ?? conversionTouch),
    last_non_direct: touch?.lastNonDirect
      ? ensureLanding(touch.lastNonDirect)
      : conversion.channel !== 'direct'
        ? ensureLanding(conversionTouch)
        : null,
    conversion: ensureLanding(conversionTouch),
  };
  if (newsletterSlice) {
    snapshot.newsletter = newsletterSlice;
  }

  return {
    analytics_session_id: sessionId,
    analytics_visitor_id: visitorId,
    attribution_snapshot: snapshot,
  };
}

export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getCartSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('cartSessionId');
}

function baseMetadata(extra?: Record<string, unknown>): Record<string, unknown> {
  const cartSessionId = getCartSessionId();
  const attribution = getStoredAttribution();
  const visitorId =
    attribution.visitor_id ||
    (hasAnalyticsConsent() ? getOrCreateAnalyticsVisitorId() : '') ||
    undefined;
  const nl = getActiveNewsletterTouch();
  return {
    device_type: getDeviceType(),
    ...(cartSessionId ? { cart_session_id: cartSessionId } : {}),
    ...attribution,
    ...(visitorId ? { visitor_id: visitorId } : {}),
    ...(nl
      ? {
          nl_c: nl.campaignId,
          nl_l: nl.linkKey,
          newsletter_campaign_id: nl.campaignId,
        }
      : {}),
    ...extra,
  };
}

function enqueue(event: Omit<QueuedEvent, 'session_id'>): void {
  if (!shouldTrackAnalytics()) return;

  const sessionId = getOrCreateAnalyticsSessionId();
  if (!sessionId) return;

  const path = normalizeAnalyticsPath(event.path);
  if (!isTrackablePath(path)) return;

  eventQueue.push({
    ...event,
    path,
    referrer_path: event.referrer_path
      ? normalizeAnalyticsPath(event.referrer_path)
      : event.referrer_path,
    session_id: sessionId,
    metadata: baseMetadata({
      ...event.metadata,
      internal_exclude: isInternalAnalyticsExcluded(),
    }),
  });
  scheduleFlush();
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushEvents();
  }, FLUSH_DELAY_MS);
}

async function flushEvents(): Promise<void> {
  if (eventQueue.length === 0) return;

  const batch = eventQueue.splice(0, 50);
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) return;

  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/track-analytics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });

    if (!res.ok && import.meta.env.DEV) {
      console.warn('[siteAnalytics] flush failed:', res.status);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[siteAnalytics] flush error:', error);
    }
  }
}

export function trackPageView(path: string): void {
  captureNewsletterTouchFromWindow();
  if (!isTrackablePath(path)) return;

  const normalizedPath = normalizeAnalyticsPath(path);
  if (!isTrackablePath(normalizedPath)) return;
  if (normalizedPath === lastTrackedPath) return;
  lastTrackedPath = normalizedPath;

  enqueue({
    event_type: 'page_view',
    path: normalizedPath,
    referrer_path: getReferrerPath(),
  });
}

function getReferrerPath(): string | null {
  if (!document.referrer) return null;
  try {
    const refUrl = new URL(document.referrer);
    if (refUrl.origin !== window.location.origin) return null;
    return normalizeAnalyticsPath(refUrl.pathname + refUrl.search);
  } catch {
    return null;
  }
}

export function trackProductView(product: AnalyticsProductInput, path?: string): void {
  if (!isValidAnalyticsProductId(product.id)) return;
  const productPath = path ?? `/produkter/${product.slug || product.id}`;
  if (!isTrackablePath(productPath)) return;

  enqueue({
    event_type: 'product_view',
    path: productPath,
    product_id: product.id,
    metadata: { product_name: product.name, category: product.category },
  });
}

export function trackProductClick(
  product: AnalyticsProductInput,
  path?: string,
  listName?: string
): void {
  const productPath = path ?? `/produkter/${product.slug || product.id}`;
  if (!isTrackablePath(productPath)) return;

  enqueue({
    event_type: 'product_click',
    path: productPath,
    product_id: product.id,
    metadata: {
      product_name: product.name,
      category: product.category,
      ...(listName ? { list_name: listName } : {}),
    },
  });
}

export function trackAddToCart(product: AnalyticsProductInput, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;
  if (!isValidAnalyticsProductId(product.id)) return;

  enqueue({
    event_type: 'add_to_cart',
    path: currentPath,
    product_id: product.id,
    metadata: { product_name: product.name, category: product.category },
  });
}

/**
 * PDP / list / grid "Boka nu" click — product_id required for CTA→booking attribution.
 * Path is the page where the CTA was clicked (PDP or listing). entry_point distinguishes surfaces.
 */
export type BookingCtaEntryPoint =
  | 'pdp'
  | 'grid'
  | 'list'
  | 'carousel'
  | 'wizard'
  | 'chatbot'
  | 'other';

export function trackBookingCtaClick(
  product: AnalyticsProductInput,
  path?: string,
  options?: { entry_point?: BookingCtaEntryPoint; list_name?: string },
): void {
  if (!isValidAnalyticsProductId(product.id)) return;
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'booking_cta_click',
    path: currentPath,
    product_id: product.id,
    metadata: {
      product_name: product.name,
      category: product.category,
      cta_label: 'Boka nu',
      entry_point: options?.entry_point ?? 'pdp',
      ...(options?.list_name ? { list_name: options.list_name } : {}),
    },
  });
}

/**
 * BookingPage loaded with a resolved catalog product.
 * product_id is the source of truth; path may still be templated to /booking/:id for page aggregates.
 */
export function trackBookingView(product: AnalyticsProductInput, path?: string): void {
  if (!isValidAnalyticsProductId(product.id)) return;
  const currentPath = path ?? `/booking/${product.id}`;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'booking_view',
    path: currentPath,
    product_id: product.id,
    metadata: {
      product_name: product.name,
      category: product.category,
      ...(product.slug ? { product_slug: product.slug } : {}),
    },
  });
}

export function trackProductLinkClick(
  product: AnalyticsProductInput,
  listName = 'Produkter'
): void {
  trackProductClick(product, undefined, listName);
  trackSelectItem(product, listName);
}

export function trackBeginCheckout(path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'begin_checkout',
    path: currentPath,
  });
}

export function trackCheckoutStepView(step: number, stepLabel: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'checkout_step_view',
    path: currentPath,
    metadata: { step, step_label: stepLabel },
  });
}

export function trackCheckoutError(step: number, errorCode: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'checkout_error',
    path: currentPath,
    metadata: { step, error_code: errorCode },
  });
}

export function trackSubmitBooking(
  value: number,
  itemsCount: number,
  customerType: 'private' | 'business',
  path?: string,
  rentalRequestId?: string
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'submit_booking',
    path: currentPath,
    metadata: {
      value,
      items_count: itemsCount,
      customer_type: customerType,
      ...(rentalRequestId ? { rental_request_id: rentalRequestId } : {}),
    },
  });
}

export function trackPurchase(value: number, path?: string, rentalRequestId?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'purchase',
    path: currentPath,
    metadata: {
      value,
      ...(rentalRequestId ? { rental_request_id: rentalRequestId } : {}),
    },
  });
}

export function trackSearch(query: string, resultCount: number, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;
  const trimmed = query.trim();
  if (!trimmed) return;

  enqueue({
    event_type: 'search',
    path: currentPath,
    metadata: { query: trimmed.toLowerCase(), result_count: resultCount },
  });
}

export function trackFaqExpand(faqId: string, question: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'faq_expand',
    path: currentPath,
    metadata: { faq_id: faqId, question },
  });
}

export function trackContactSubmit(path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'contact_submit',
    path: currentPath,
  });
}

function quoteFormMetadata(
  ctx: QuoteFormAnalyticsContext,
  path: string,
  extra?: Record<string, unknown>
): Record<string, unknown> {
  return {
    source_page: path,
    variant: ctx.variant ?? 'modal',
    ...(ctx.productName ? { product_name: ctx.productName } : {}),
    ...(ctx.packageType ? { package_type: ctx.packageType } : {}),
    ...(ctx.eventType ? { event_type: ctx.eventType } : {}),
    ...(ctx.quoteId ? { quote_id: ctx.quoteId } : {}),
    ...(ctx.closeMethod ? { close_method: ctx.closeMethod } : {}),
    ...(ctx.partner ? { partner: ctx.partner } : {}),
    ...(ctx.budgetSegment ? { budget_segment: ctx.budgetSegment } : {}),
    ...(ctx.ctaContext ? { cta_context: ctx.ctaContext } : {}),
    ...extra,
  };
}

export function trackQuoteRequest(productId?: string, quoteId?: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'quote_request',
    path: currentPath,
    product_id: productId ?? null,
    metadata: quoteId ? { quote_id: quoteId } : undefined,
  });
}

export function trackQuoteCtaClick(
  productId?: string | null,
  path?: string,
  extra?: Record<string, unknown>
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'quote_cta_click',
    path: currentPath,
    product_id: productId ?? null,
    metadata: {
      source_page: currentPath,
      ...extra,
    },
  });
}

export function trackQuoteModalOpen(ctx: QuoteFormAnalyticsContext = {}, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'quote_modal_open',
    path: currentPath,
    product_id: ctx.productId ?? null,
    metadata: quoteFormMetadata(ctx, currentPath),
  });
}

export function trackQuoteFormStarted(ctx: QuoteFormAnalyticsContext = {}, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'quote_form_started',
    path: currentPath,
    product_id: ctx.productId ?? null,
    metadata: quoteFormMetadata(ctx, currentPath),
  });
}

export function trackQuoteFormSubmitted(ctx: QuoteFormAnalyticsContext = {}, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'quote_form_submitted',
    path: currentPath,
    product_id: ctx.productId ?? null,
    metadata: quoteFormMetadata(ctx, currentPath),
  });
}

/** Technical failure after a real submit attempt. No PII in metadata. */
export function classifyQuoteFormError(error: unknown): {
  error_code: string | null;
  error_type: string;
} {
  if (error && typeof error === 'object') {
    const e = error as {
      code?: unknown;
      message?: unknown;
      name?: unknown;
      status?: unknown;
    };
    const code = typeof e.code === 'string' || typeof e.code === 'number' ? String(e.code) : null;
    const message = typeof e.message === 'string' ? e.message.toLowerCase() : '';
    const name = typeof e.name === 'string' ? e.name.toLowerCase() : '';

    if (code === '42501' || message.includes('row-level security')) {
      return { error_code: code ?? '42501', error_type: 'rls' };
    }
    if (code?.startsWith('PGRST') || message.includes('schema cache')) {
      return { error_code: code, error_type: 'postgrest' };
    }
    if (code?.startsWith('23') || message.includes('duplicate key') || message.includes('unique constraint')) {
      return { error_code: code, error_type: 'constraint' };
    }
    if (name === 'typeerror' || message.includes('failed to fetch') || message.includes('network')) {
      return { error_code: code, error_type: 'network' };
    }
    if (code) {
      return { error_code: code, error_type: 'supabase' };
    }
  }
  return { error_code: null, error_type: 'unknown' };
}

export function trackQuoteFormError(
  ctx: QuoteFormAnalyticsContext = {},
  error?: unknown,
  path?: string
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  const { error_code, error_type } = classifyQuoteFormError(error);

  enqueue({
    event_type: 'quote_form_error',
    path: currentPath,
    product_id: ctx.productId ?? null,
    metadata: quoteFormMetadata(ctx, currentPath, {
      error_type,
      ...(error_code ? { error_code } : {}),
    }),
  });
}

export function trackQuoteFormClosed(ctx: QuoteFormAnalyticsContext = {}, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'quote_form_closed',
    path: currentPath,
    product_id: ctx.productId ?? null,
    metadata: quoteFormMetadata(ctx, currentPath),
  });
}

export function trackChatbotOpen(path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'chatbot_open',
    path: currentPath,
  });
}

export function trackChatbotHumanRequest(path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'chatbot_human_request',
    path: currentPath,
  });
}

export function trackCustomerTypeSelect(customerType: 'private' | 'business', path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'customer_type_select',
    path: currentPath,
    metadata: { customer_type: customerType },
  });
}

export function trackCtaClick(label: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'cta_click',
    path: currentPath,
    metadata: { cta_label: label },
  });
}

function viewportSegment(): 'mobile' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
}

/** Homepage booking bar — quote_request funnel (väg A). */
export function trackHomeBookingBarDateSelect(date: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;
  enqueue({
    event_type: 'home_booking_bar_date_select',
    path: currentPath,
    metadata: { date, viewport: viewportSegment(), funnel: 'quote_request' },
  });
}

export function trackHomeBookingBarEventSelect(eventType: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;
  enqueue({
    event_type: 'home_booking_bar_event_select',
    path: currentPath,
    metadata: { event_type: eventType, viewport: viewportSegment(), funnel: 'quote_request' },
  });
}

export function trackHomeBookingBarCtaClick(
  extra: { hasDate: boolean; eventType: string | null },
  path?: string
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;
  enqueue({
    event_type: 'home_booking_bar_cta_click',
    path: currentPath,
    metadata: {
      cta_label: 'Få ett personligt förslag',
      has_date: extra.hasDate,
      event_type: extra.eventType,
      viewport: viewportSegment(),
      funnel: 'quote_request',
    },
  });
}

/** Homepage popular cards (väg B). */
export function trackHomePopularPackageClick(
  productId: string,
  ctaLabel: 'Se paket' | 'Boka paket',
  path?: string
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;
  enqueue({
    event_type: 'home_popular_package_click',
    path: currentPath,
    product_id: productId,
    metadata: {
      cta_label: ctaLabel,
      viewport: viewportSegment(),
      funnel: 'package',
    },
  });
}

export function trackWizardStep(step: number, stepId: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'wizard_step',
    path: currentPath,
    metadata: { step, step_id: stepId },
  });
}

export function trackWizardComplete(path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'wizard_complete',
    path: currentPath,
  });
}

export function trackWizardAbandon(step: number, stepId: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'wizard_abandon',
    path: currentPath,
    metadata: { step, step_id: stepId },
  });
}

export function trackLandingView(landingPath: string): void {
  if (!isTrackablePath(landingPath)) return;

  enqueue({
    event_type: 'landing_view',
    path: landingPath,
  });
}

export function trackForetagNeedCardsView(path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'foretag_need_cards_view',
    path: currentPath,
  });
}

const FORETAG_LANDING = '/foretag';

/**
 * Phase 2A commercial funnel helpers.
 * Prefer dedicated event_type when edge/DB allow; always include funnel_event for reporting.
 * Dual-write to cta_click / quote_cta_click so events survive before migration is applied.
 */
export function trackForetagHeroCtaClick(
  intent: 'planning_call' | 'quote',
  ctaLabel: string,
  path: string = FORETAG_LANDING,
): void {
  if (!isTrackablePath(path)) return;
  const meta = {
    funnel_event: 'foretag_hero_cta_click',
    intent,
    cta_label: ctaLabel,
    source_page: path,
  };
  enqueue({ event_type: 'foretag_hero_cta_click', path, metadata: meta });
  enqueue({ event_type: 'cta_click', path, metadata: meta });
}

export function trackPlanningCallStart(
  ctaLabel: string,
  placement: string,
  path: string = FORETAG_LANDING,
): void {
  if (!isTrackablePath(path)) return;
  const meta = {
    funnel_event: 'planning_call_start',
    cta_label: ctaLabel,
    placement,
    package_type: 'Planeringssamtal',
    source_page: path,
  };
  enqueue({ event_type: 'planning_call_start', path, metadata: meta });
  enqueue({
    event_type: 'quote_cta_click',
    path,
    product_id: null,
    metadata: meta,
  });
}

export function trackQuoteStart(
  ctaLabel: string,
  placement: string,
  path: string = FORETAG_LANDING,
): void {
  if (!isTrackablePath(path)) return;
  const meta = {
    funnel_event: 'quote_start',
    cta_label: ctaLabel,
    placement,
    package_type: 'Företagspaket',
    source_page: path,
  };
  enqueue({ event_type: 'quote_start', path, metadata: meta });
  enqueue({
    event_type: 'quote_cta_click',
    path,
    product_id: null,
    metadata: meta,
  });
}

export function trackSpecialistNavigation(
  destination: string,
  label: string,
  path: string = FORETAG_LANDING,
): void {
  if (!isTrackablePath(path)) return;
  const meta = {
    funnel_event: 'specialist_navigation',
    destination,
    cta_label: label,
    source_page: path,
  };
  enqueue({ event_type: 'specialist_navigation', path, metadata: meta });
  enqueue({ event_type: 'cta_click', path, metadata: meta });
}

export function trackPhoneClick(placement: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;
  const meta = {
    funnel_event: 'phone_click',
    cta_label: 'phone',
    placement,
    source_page: currentPath,
  };
  enqueue({ event_type: 'phone_click', path: currentPath, metadata: meta });
  enqueue({ event_type: 'cta_click', path: currentPath, metadata: meta });
}

export function trackEmailClick(placement: string, path?: string): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;
  const meta = {
    funnel_event: 'email_click',
    cta_label: 'email',
    placement,
    source_page: currentPath,
  };
  enqueue({ event_type: 'email_click', path: currentPath, metadata: meta });
  enqueue({ event_type: 'cta_click', path: currentPath, metadata: meta });
}

export function trackForetagNeedCardClick(
  metadata: {
    card: 'foretagsevent' | 'konferens' | 'massa';
    ctaContext: string;
    destinationUrl: string;
  },
  path?: string,
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'foretag_need_card_click',
    path: currentPath,
    metadata: {
      card: metadata.card,
      cta_context: metadata.ctaContext,
      destination_url: metadata.destinationUrl,
    },
  });
}

export type PortalUpsellAnalyticsItem = {
  itemType: 'catalog_product' | 'addon_template';
  refId: string;
  name: string;
  unitPrice?: number;
};

function portalUpsellMetadata(
  item: PortalUpsellAnalyticsItem,
  bookingId?: string | null,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    source: 'common_addons',
    item_type: item.itemType,
    item_id: item.refId,
    item_name: item.name,
    ...(item.unitPrice != null ? { value: item.unitPrice } : {}),
    ...(bookingId ? { booking_id: bookingId } : {}),
    ...extra,
  };
}

export function trackPortalUpsellView(
  items: PortalUpsellAnalyticsItem[],
  bookingId?: string | null,
  path?: string,
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'portal_upsell_view',
    path: currentPath,
    metadata: {
      source: 'common_addons',
      item_count: items.length,
      item_ids: items.map((i) => i.refId),
      ...(bookingId ? { booking_id: bookingId } : {}),
    },
  });
}

export function trackPortalUpsellAddClick(
  item: PortalUpsellAnalyticsItem,
  bookingId?: string | null,
  path?: string,
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'portal_upsell_add_click',
    path: currentPath,
    product_id: item.itemType === 'catalog_product' ? item.refId : null,
    metadata: portalUpsellMetadata(item, bookingId),
  });
}

export function trackPortalUpsellAddSuccess(
  item: PortalUpsellAnalyticsItem,
  bookingId?: string | null,
  path?: string,
): void {
  const currentPath = path ?? window.location.pathname;
  if (!isTrackablePath(currentPath)) return;

  enqueue({
    event_type: 'portal_upsell_add_success',
    path: currentPath,
    product_id: item.itemType === 'catalog_product' ? item.refId : null,
    metadata: portalUpsellMetadata(item, bookingId),
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (eventQueue.length > 0) {
      void flushEvents();
    }
  });
}
